import type { CSSProperties } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { useChildren, useSummary, useExponentByCode } from '../api/queries'
import { formatMoney } from '../lib/money'
import { kidAccent } from '../lib/kidAccent'

export function DashboardPage() {
  const { data: children, isLoading: childrenLoading } = useChildren()
  const { data: summary, isLoading: summaryLoading } = useSummary()
  const exponentByCode = useExponentByCode()

  if (childrenLoading || summaryLoading) return <p className="loading">Loading…</p>

  const nameById = new Map((children ?? []).map((c) => [c.id, c.displayName]))

  return (
    <div className="dashboard">
      <h1>Balances</h1>
      <div className="balance-grid">
        {(summary?.balances ?? []).map((b) => {
          const style = { '--kid-accent': kidAccent(b.childId) } as CSSProperties
          return (
            <Link key={b.childId} to={`/children/${b.childId}`} className="balance-card" style={style}>
              <span className="child-name">{nameById.get(b.childId) ?? `Child #${b.childId}`}</span>
              <span className={`balance-amount${b.balance.minorUnits < 0 ? ' negative' : ''}`}>
                {formatMoney(b.balance, exponentByCode)}
              </span>
            </Link>
          )
        })}
      </div>

      <AddChildForm nextSortOrder={children?.length ?? 0} />
    </div>
  )
}

function AddChildForm({ nextSortOrder }: { nextSortOrder: number }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      // displayName mirrors name - there's no separate UI need for them to differ yet, and
      // the server keeps them as distinct fields only because `name` is the unique key.
      const { error: apiError } = await api.POST('/children', {
        body: { name, displayName: name, sortOrder: nextSortOrder },
      })
      if (apiError) throw new Error(apiError.detail ?? apiError.title)
    },
    onSuccess: () => {
      setName('')
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['children'] })
      queryClient.invalidateQueries({ queryKey: ['summary'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <form
      className="add-transaction"
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
    >
      <input
        type="text"
        placeholder="New child's name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <button type="submit" disabled={mutation.isPending || name.trim().length === 0}>
        Add child
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  )
}
